import React, { useState, useEffect } from 'react';
import { useAuth } from '../Contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const KeywordsManager = () => {
  const [keywords, setKeywords] = useState([]);
  const [newKeywords, setNewKeywords] = useState('');
  const [separator, setSeparator] = useState('space'); // space or comma
  const [submitMode, setSubmitMode] = useState('replace'); // replace, append, or merge
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    // Load existing keywords
    const loadKeywords = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/keywords`);
        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }
        const data = await response.json();
        setKeywords(data.keywords || []);
      } catch (error) {
        console.error('Error loading keywords:', error);
        setMessage('Error loading keywords: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadKeywords();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Parse the input based on the selected separator
    const separatorChar = separator === 'comma' ? ',' : ' ';
    const parsedKeywords = newKeywords
      .split(separatorChar)
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    if (parsedKeywords.length === 0) {
      setMessage('Please enter at least one keyword');
      return;
    }
    
    // Process according to submit mode
    let updatedKeywords = [];
    
    switch (submitMode) {
      case 'replace':
        updatedKeywords = parsedKeywords;
        break;
      case 'append':
        updatedKeywords = [...keywords, ...parsedKeywords];
        break;
      case 'merge':
        updatedKeywords = Array.from(new Set([...keywords, ...parsedKeywords]));
        break;
      default:
        updatedKeywords = parsedKeywords;
    }
    
    await saveKeywords(updatedKeywords);
  };

  // New function to handle keyword deletion
  const handleDeleteKeyword = async (indexToDelete) => {
    const updatedKeywords = keywords.filter((_, index) => index !== indexToDelete);
    await saveKeywords(updatedKeywords);
  };
  
  // Extracted the save keywords functionality to reuse it
  const saveKeywords = async (updatedKeywords) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/keywords`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeader() // Add authorization header
        },
        body: JSON.stringify({ keywords: updatedKeywords })
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      await response.json();
      
      // Update local state
      setKeywords(updatedKeywords);
      setNewKeywords('');
      setMessage(`Keywords updated successfully!`);
      
      // Clear success message after a few seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving keywords:', error);
      setMessage('Error saving keywords: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const getSeparatorExample = () => {
    return separator === 'comma' 
      ? 'e.g., genomics, DNA analysis, health' 
      : 'e.g., genomics DNA analysis health';
  };
  
  return (
    <div className="max-w-4xl mx-auto p-6">
      {message && (
        <div className={`p-4 mb-6 rounded ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {message}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-4">Current Keywords</h2>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="flex flex-wrap mb-4">
            {keywords.length > 0 ? (
              keywords.map((keyword, index) => (
                <span key={index} className="px-3 py-1 m-1 bg-gray-100 rounded-full text-gray-800 flex items-center">
                  {keyword}
                  <button 
                    onClick={() => handleDeleteKeyword(index)} 
                    className="ml-2 text-red-500 font-bold hover:text-red-700 focus:outline-none"
                    aria-label={`Delete keyword ${keyword}`}
                  >
                    −
                  </button>
                </span>
              ))
            ) : (
              <p className="text-gray-500">No keywords defined yet.</p>
            )}
          </div>
        )}
        <p className="text-sm text-gray-600">Total: {keywords.length} keywords</p>
      </div>
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Add or Update Keywords</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Input Format:</label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="separator"
                  value="space"
                  checked={separator === 'space'}
                  onChange={() => setSeparator('space')}
                />
                <span className="ml-2">Space Separated</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="separator"
                  value="comma"
                  checked={separator === 'comma'}
                  onChange={() => setSeparator('comma')}
                />
                <span className="ml-2">Comma Separated</span>
              </label>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">Update Method:</label>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="submitMode"
                  value="replace"
                  checked={submitMode === 'replace'}
                  onChange={() => setSubmitMode('replace')}
                />
                <span className="ml-2">Replace All</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="submitMode"
                  value="append"
                  checked={submitMode === 'append'}
                  onChange={() => setSubmitMode('append')}
                />
                <span className="ml-2">Append to Existing</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="submitMode"
                  value="merge"
                  checked={submitMode === 'merge'}
                  onChange={() => setSubmitMode('merge')}
                />
                <span className="ml-2">Merge & Remove Duplicates</span>
              </label>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              Enter Keywords ({getSeparatorExample()}):
            </label>
            <textarea
              className="w-full p-2 border rounded-md"
              rows="4"
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
              placeholder={`Enter keywords separated by ${separator === 'comma' ? 'commas' : 'spaces'}...`}
            />
          </div>
          
          <button
            type="submit"
            className={`${isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white font-bold py-2 px-4 rounded flex items-center`}
            disabled={isLoading}
          >
            {isLoading && (
              <span className="mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            )}
            {isLoading ? 'Saving...' : 'Save Keywords'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default KeywordsManager;